import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GameCard from "./GameCard";
import { games } from "@/data/gamesData";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useIsMobile, useIsExtraSmall } from "@/hooks/use-mobile";

const GamesSection = () => {
  const isMobile = useIsMobile();
  const isXs = useIsExtraSmall();
  const [displayedGames, setDisplayedGames] = useState(games.slice(0, isMobile ? 4 : 8));
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredGames, setFilteredGames] = useState(games);
  
  useEffect(() => {
    const filtered = games.filter(game => 
      game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      game.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredGames(filtered);
    // Show fewer games on smaller screens
    setDisplayedGames(filtered.slice(0, isMobile ? 4 : 8));
  }, [searchQuery, isMobile]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <motion.section 
      id="games" 
      className="py-6 md:py-12 px-3 md:px-8 relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div 
          className="absolute w-full h-full opacity-20"
          style={{ 
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(155, 135, 245, 0.3) 0%, transparent 50%), 
                             radial-gradient(circle at 75% 75%, rgba(45, 212, 191, 0.3) 0%, transparent 50%)`
          }}
        />
        
        {/* Floating neural networks */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <motion.path 
            d="M10,30 Q30,10 50,30 T90,30"
            stroke="#9b87f5" 
            strokeWidth="0.2" 
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.2 }}
            transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
          <motion.path 
            d="M10,50 Q40,30 50,50 T90,50"
            stroke="#2dd4bf" 
            strokeWidth="0.2" 
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.2 }}
            transition={{ duration: 3, delay: 1, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
          <motion.path 
            d="M10,70 Q50,40 50,70 T90,70"
            stroke="#9b87f5" 
            strokeWidth="0.2" 
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.2 }}
            transition={{ duration: 3, delay: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
        </svg>
        
        {/* Animated circles */}
        <motion.div 
          className="absolute w-40 h-40 rounded-full bg-brain-purple/5 -top-20 -left-20"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute w-64 h-64 rounded-full bg-brain-light-purple/10 bottom-10 -right-32"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-8 gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="h-1 w-6 md:w-10 bg-brain-purple rounded-full"></div>
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-brain-purple to-brain-blue bg-clip-text text-transparent">
              Brain Training Games
            </h2>
          </div>
          
          <div className="hidden md:flex relative w-64">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              placeholder="Search games..." 
              className="pl-10 bg-white border-brain-light-purple/30"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 max-w-2xl">
          Each game is designed to target specific cognitive functions. Play regularly to see improvements in your memory, focus, problem-solving, and more.
        </p>
        
        <div className="md:hidden relative w-full mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input 
            placeholder="Search games..." 
            className="pl-10 bg-white border-brain-light-purple/30"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {filteredGames.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No games found matching "{searchQuery}"</p>
          </div>
        ) : (
          <>
            <motion.div 
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
              variants={container}
              initial="hidden"
              animate="show"
            >
              {displayedGames.map((game) => (
                <motion.div key={game.id} variants={item}>
                  <GameCard game={game} />
                </motion.div>
              ))}
            </motion.div>
{/*             
            {filteredGames.length > displayedGames.length && (
              <div className="flex justify-center mt-4 md:mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setDisplayedGames(filteredGames.slice(0, displayedGames.length + (isMobile ? 2 : 4)))}
                  className="border-brain-light-purple/30 hover:bg-brain-soft-purple/20 text-sm"
                >
                  Show More Games
                </Button>
              </div>
            )} */}
            
            <div className="flex justify-center mt-6 md:mt-8">
              <Button asChild className="bg-brain-purple hover:bg-brain-vivid-purple text-white">
                <Link to="/games">View All Games</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.section>
  );
};

export default GamesSection;
